import { useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDialog, usePlatformStore } from "@platform/store";
import type {
  SearchablePage,
  SearchResultItem,
} from "@core/features/support";
import { useFetchSupportArticles } from "@core/features/support";
import { useSupportSheet } from "@platform/store";
import {
  useFetchActions,
  getOrganizationsFromActions,
} from "@core/features/actions";
import { useIsSupportPage } from "./useIsSupportPage";
import { SupportSearchForm } from "./SupportSearchForm";

export function SupportSearchFormContainer() {
  const { activeDialog, closeDialog } = useDialog();

  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  // Only treat the URL :id as a programId on program routes
  const isProgramRoute =
    location.pathname.startsWith("/dashboard/") ||
    location.pathname.startsWith("/program/");
  const urlProgramId =
    isProgramRoute && id ? Number(id) : undefined;

  const isSupportPage = useIsSupportPage();

  const { open: openSheet } = useSupportSheet();

  const user = usePlatformStore((s) => s.user);

  const { data } = useFetchSupportArticles();
  const articles = data?.articles ?? [];
  const categories = data?.categories ?? [];

  // Fetch user actions — scoped to current program when on a program route
  const { data: actionsData } = useFetchActions({
    programId: urlProgramId,
    user,
    isEnabled: !!user,
  });

  // Get first available program ID from user's actions
  const programId = useMemo(() => {
    if (!actionsData?.actions || actionsData.actions.length === 0) return null;
    return actionsData.actions[0]?.program ?? null;
  }, [actionsData?.actions]);

  // Get organization data if user has org admin access
  const organizations = useMemo(() => {
    if (!actionsData?.actions) return [];
    return getOrganizationsFromActions(actionsData.actions);
  }, [actionsData?.actions]);

  // Build list of searchable pages based on user access
  const searchablePages: SearchablePage[] = useMemo(() => {
    const pages: SearchablePage[] = [
      {
        id: "dashboard",
        title: "Dashboard",
        description: "View your tasks and program overview",
        href: "/dashboard",
      },
      {
        id: "support",
        title: "Support",
        description: "Get help and browse support articles",
        href: "/support",
      },
      {
        id: "account",
        title: "Account Settings",
        description: "Manage your account preferences and profile",
        href: "/account",
      },
    ];

    // Add program-dependent pages if user has a program
    if (programId) {
      pages.push(
        {
          id: "program",
          title: "Program",
          description: "Access your program schedule and circle",
          href: `/program/${programId}`,
        },
        {
          id: "profile",
          title: "Profile",
          description: "View and edit your program profile",
          href: `/program/${programId}/profile`,
        },
        {
          id: "resources",
          title: "Resources",
          description: "Browse program resources and materials",
          href: `/program/${programId}/resources`,
        }
      );
    }

    // Add org admin pages if user has access
    for (const org of organizations) {
      pages.push({
        id: `organization-admin-${org.id}`,
        title: org.name ? `${org.name} Admin` : "Organization Admin",
        description: "Manage organization settings and members",
        href: `/program/${org.programId}/organization/${org.id}/admin`,
      });
    }

    return pages;
  }, [programId, organizations]);

  const isOpen = activeDialog === "supportSearch";

  const handleClose = () => {
    closeDialog();
  };

  const isSearchablePage = (item: SearchResultItem): item is SearchablePage => {
    return "href" in item;
  };

  const handleResultClick = (item: SearchResultItem) => {
    closeDialog();

    if (isSearchablePage(item)) {
      navigate(item.href);
    } else {
      // It's an Article
      isSupportPage
        ? navigate(`/support/${item.id}`)
        : openSheet(item.id);
    }
  };

  return (
    <SupportSearchForm
      articles={articles}
      categories={categories}
      pages={searchablePages}
      isOpen={isOpen}
      onClose={handleClose}
      onResultClick={handleResultClick}
    />
  );
}

export default SupportSearchFormContainer;

